import {
  IChecklistItemPopulated,
  IChecklistItemRow,
  IStampSettings,
  onChecklistRowChange,
} from "@mctypes/checklist.types";
import { IMinUserDepricated, Paths, PathValue } from "@mctypes/global.types";
import { Card, Grid, Stack, Text, Title } from "@mantine/core";
import React from "react";
import { McTitle } from "../../../../../components";
import FoldableSection from "../../../../../components/FoldableSection/FoldableSection";
import { LinkifiedText } from "../../../../../components/LinkifiedText";
import McInputNew from "../../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../../components/McInput/McTextAreaNew";
import { useAppSelector } from "../../../../../hooks";
import { ICheckTypes } from "@mcapps/shared/schemas/checklistSchema";
import ChecklistDetailsRowChecksSection from "./ChecklistDetailsRowChecksSection";

const ChecklistDetailsLegacy: React.FC<{
  checklistRow: IChecklistItemRow;
  isItem: boolean;
  isTemplate: boolean;
  onAssignedToChange: <T extends keyof IChecklistItemRow>(
    path: string,
    newValue: IChecklistItemRow[T],
    oldValue: string,
  ) => void;
  editable: boolean;
  updateRowAttribute: <P extends Paths<IChecklistItemPopulated, 4>>(
    rowId: string,
    path: P,
    oldValue: string,
    newValue: PathValue<IChecklistItemPopulated, P>,
  ) => void;
  stampSettings: IStampSettings;
  checkTypes: ICheckTypes;
}> = ({
  checklistRow,
  onAssignedToChange,
  updateRowAttribute,
  isItem,
  isTemplate,
  editable,
  stampSettings,
  checkTypes,
}) => {
  const project = useAppSelector((state) => state.adminReducer.project);

  const memberUsers = (project.members ?? [])
    .map((member) => member.user)
    .filter((user): user is IMinUserDepricated => typeof user !== "string" && user != null);

  const updateRowParameter: onChecklistRowChange = async (oldValue, newValue, parameter) => {
    if (oldValue === newValue) {
      return;
    }

    if (isItem === true) {
      updateRowAttribute(
        checklistRow._id,
        parameter as Paths<IChecklistItemPopulated, 4>,
        oldValue?.toString() ?? "",
        newValue as PathValue<IChecklistItemPopulated, Paths<IChecklistItemPopulated, 4>>,
      );
    } else {
      onAssignedToChange(parameter, newValue, oldValue?.toString() ?? "");
    }
  };

  const referenceValue = typeof checklistRow.reference === "string" ? checklistRow.reference.trim() : "";

  return (
    <Stack gap="sm">
      <FoldableSection storageKey="checklist-modal-details">
        <FoldableSection.Header>
          <Title order={6}>Detaljer</Title>
        </FoldableSection.Header>

        <FoldableSection.Body>
          <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
            <Stack gap="sm">
              <Grid>
                <Grid.Col span={12}>
                  <McTitle title="Tittel">
                    {editable === true ? (
                      <Stack gap={6}>
                        {isItem === true ? (
                          <McTextAreaNew
                            required={true}
                            autosize
                            onBlur={(e) => updateRowParameter(checklistRow.title, e.target.value, "title")}
                            value={checklistRow.title}
                          />
                        ) : (
                          <McInputNew
                            required={true}
                            onBlur={(e) => updateRowParameter(checklistRow.title, e.target.value, "title")}
                            value={checklistRow.title}
                          />
                        )}

                        {isTemplate !== true && (
                          <McInputNew
                            required={false}
                            placeholder="Referanse"
                            onBlur={(e) => updateRowParameter(checklistRow.reference, e.target.value, "reference")}
                            value={checklistRow.reference ?? ""}
                          />
                        )}
                      </Stack>
                    ) : (
                      <Stack gap={2}>
                        <Text size="sm">
                          <LinkifiedText text={checklistRow.title} />
                        </Text>
                        {isTemplate !== true && referenceValue.length > 0 && (
                          <Text size="xs" c="dimmed">
                            <LinkifiedText text={referenceValue} />
                          </Text>
                        )}
                      </Stack>
                    )}
                  </McTitle>
                </Grid.Col>
              </Grid>

              <ChecklistDetailsRowChecksSection
                updateRowAttribute={updateRowAttribute}
                onAssignedToChange={onAssignedToChange}
                checkTypes={checkTypes}
                users={memberUsers}
                isItem={isItem}
                isTemplate={isTemplate}
                checklistRow={checklistRow}
                editable={editable}
                onValueChange={updateRowParameter}
                stampSettings={stampSettings}
              />
            </Stack>
          </Card>
        </FoldableSection.Body>
      </FoldableSection>

    </Stack>
  );
};

export default ChecklistDetailsLegacy;
